import { useState } from "react";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

/**
 * Modal form that lets any visitor send a Sales / Licensing inquiry.
 * Hits `POST /api/sales/inquiry`, which relays the message to the
 * `sales_manager_email` configured in Admin → Sales / Licensing and
 * persists it in the `sales_inquiries` collection.
 */
export function SalesInquiryDialog({ open, onOpenChange }) {
    const [name, setName] = useState("");
    const [organization, setOrganization] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    // Honeypot: legitimate humans never see or fill this field. Bots typically
    // fill every input on a form. When populated, the backend silently drops
    // the request without emailing or persisting anything.
    const [website, setWebsite] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setName(""); setOrganization(""); setEmail(""); setPhone(""); setMessage(""); setWebsite("");
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!name.trim() || !email.trim() || !message.trim()) {
            toast.error("Name, email and message are required");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/sales/inquiry", {
                name: name.trim(),
                organization: organization.trim() || undefined,
                email: email.trim(),
                phone: phone.trim() || undefined,
                message: message.trim(),
                website: website || undefined,  // honeypot passthrough
            });
            if (data.delivered) {
                toast.success("Thanks — your inquiry has been sent to our sales team. We'll reach out shortly.");
            } else {
                toast.success("Thanks — your inquiry has been recorded. Our team will follow up shortly.");
            }
            reset();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not submit your inquiry — please try again");
        }
        setBusy(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
            <DialogContent className="sm:max-w-lg" data-testid="sales-inquiry-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <EnvelopeSimple size={22} weight="duotone" className="text-civic-700" />
                        <DialogTitle className="font-heading text-2xl">Inquire about Sales / Licensing</DialogTitle>
                    </div>
                    <DialogDescription>
                        Tell us a bit about your organization and use case. Our sales team will reach back out to the email address you provide.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <div>
                        <label htmlFor="si-name" className="overline text-slate-500 block mb-1">Your name *</label>
                        <Input id="si-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="sales-inquiry-name" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="si-org" className="overline text-slate-500 block mb-1">Organization</label>
                            <Input id="si-org" value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="County, HOA, school…" data-testid="sales-inquiry-org" />
                        </div>
                        <div>
                            <label htmlFor="si-phone" className="overline text-slate-500 block mb-1">Phone</label>
                            <Input id="si-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional" data-testid="sales-inquiry-phone" />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="si-email" className="overline text-slate-500 block mb-1">Email *</label>
                        <Input id="si-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="sales-inquiry-email" />
                    </div>
                    <div>
                        <label htmlFor="si-message" className="overline text-slate-500 block mb-1">How can we help? *</label>
                        <Textarea
                            id="si-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            maxLength={5000}
                            placeholder="Tell us about your election, expected voter count, timeline, and anything else that would help us route your inquiry."
                            required
                            data-testid="sales-inquiry-message"
                        />
                        <div className="text-[10px] hash-mono text-slate-400 mt-1 text-right">{message.length} / 5000</div>
                    </div>
                    {/* Honeypot — invisible to humans (off-screen, aria-hidden,
                        tabIndex=-1, autoComplete=off). Bots that autofill every
                        input will populate this field and the backend will drop
                        the request without emailing or persisting anything. */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                        <label htmlFor="si-website">Website (leave blank)</label>
                        <input
                            id="si-website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            data-testid="sales-inquiry-honeypot"
                        />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={busy}
                            data-testid="sales-inquiry-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={busy}
                            className="bg-civic-600 hover:bg-civic-700 text-white"
                            data-testid="sales-inquiry-submit"
                        >
                            <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                            {busy ? "Sending…" : "Send inquiry"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Compact CTA button that opens the SalesInquiryDialog. Handy so callers
 * don't have to repeat the `useState(false)` + `<SalesInquiryDialog>` boilerplate.
 */
export function SalesInquiryButton({ className = "", size = "lg", label = "Inquire about Sales / Licensing", testId = "sales-inquiry-open-btn" }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size={size}
                onClick={() => setOpen(true)}
                className={`bg-civic-600 hover:bg-civic-700 text-white ${className}`}
                data-testid={testId}
            >
                <EnvelopeSimple size={16} className="mr-2" weight="fill" />
                {label}
            </Button>
            <SalesInquiryDialog open={open} onOpenChange={setOpen} />
        </>
    );
}
